import QRCode from 'react-qr-code'
import { useEffect } from 'react'
import { AlertCircle } from 'lucide-react'
import type { PaymentProvider } from '@/types/payment'
import type { CreateNowPaymentData, Currency } from '@/types/payment'
import { DepositCopyField } from '@/components/deposit/DepositCopyField'
import { getDepositActionButtonClassName } from '@/components/deposit/styles'
import { useLoader } from '@/context/LoaderContext'
import { useMetaMaskDeposit } from '@/hooks/use-metamask-deposit'
import { usePhantomDeposit } from '@/hooks/use-phantom-deposit'

interface PaymentStepProps {
	provider: PaymentProvider
	currency: Currency
	currencyLabel: string
	amount: string
	nowPaymentData: CreateNowPaymentData | null
	metamaskAddress: string | null
	onRetry: () => void
	hasSubmitError: boolean
}

export function PaymentStep({ provider, currency, currencyLabel, amount, nowPaymentData, metamaskAddress, onRetry, hasSubmitError }: PaymentStepProps) {
	const { showLoader, hideLoader } = useLoader()
	const {
		payWithMetaMask,
		isSubmitting: isMetaMaskSubmitting,
		lastError: metaMaskError,
	} = useMetaMaskDeposit({
		currency,
		amount,
		adminAddress: metamaskAddress,
	})
	const {
		payWithPhantom,
		isSubmitting: isPhantomSubmitting,
		lastError: phantomError,
	} = usePhantomDeposit({
		currency,
		amount,
		adminAddress: metamaskAddress,
	})

	const isPaying = isMetaMaskSubmitting || isPhantomSubmitting

	useEffect(() => {
		if (!isPaying) {
			return
		}

		showLoader()
		return () => hideLoader()
	}, [isPaying, showLoader, hideLoader])

	if (hasSubmitError) {
		return (
			<div className='flex min-h-0 flex-1 flex-col items-center justify-center gap-4 text-center'>
				<p className='text-sm text-destructive'>Unable to prepare your deposit. Please try again.</p>
				<button type='button' onClick={onRetry} className={getDepositActionButtonClassName(true)}>
					<span>Retry</span>
					<span className='bg-current/50 mt-0.5 h-0.5 w-8 rounded-full' aria-hidden />
				</button>
			</div>
		)
	}

	if (provider === 'nowpayments' && nowPaymentData) {
		const paymentHistory = nowPaymentData.savedPaymentHistory

		return (
			<div className='flex min-h-0 flex-1 flex-col gap-5'>
				<p className='text-center text-sm leading-6 tracking-[-0.21px] text-muted-foreground'>
					Send the amount of {currency.currency_code} in the {currency.network} network to the following address to receive the equivalent in coins
				</p>

				<div className='mx-auto rounded-2xl border border-border bg-secondary p-4'>
					<QRCode value={nowPaymentData.pay_uri} size={180} bgColor='transparent' fgColor='#ffffff' />
				</div>

				<div className='flex items-start gap-3 rounded-xl border border-warning/30 bg-warning/10 px-4 py-3'>
					<span className='mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-warning text-background'>
						<AlertCircle className='h-3.5 w-3.5' />
					</span>
					<p className='text-sm leading-6 text-warning'>Top-up addresses are one-time use only. Don&apos;t reuse it, as your account won&apos;t be credited.</p>
				</div>

				<DepositCopyField label={`Your personal ${currency.currency_code} deposit address`} value={paymentHistory.pay_address} />
				<DepositCopyField label='Deposit amount' value={paymentHistory.amount} />

				<div className='rounded-xl border border-border bg-secondary px-4 py-3 text-center'>
					<p className='text-sm font-medium tracking-[-0.21px] text-muted-foreground'>Waiting for payment</p>
					<p className='mt-1 text-sm font-semibold capitalize tracking-[-0.21px] text-foreground'>{paymentHistory.status}</p>
				</div>
			</div>
		)
	}

	if (provider === 'metamask' && metamaskAddress) {
		return (
			<div className='flex min-h-0 flex-1 flex-col gap-5'>
				<p className='text-center text-sm leading-6 tracking-[-0.21px] text-muted-foreground'>
					Transfer {amount} {currency.currency_code} on the {currency.network} network to the admin wallet address below.
				</p>

				<DepositCopyField label={`Admin ${currency.currency_code} deposit address`} value={metamaskAddress} />
				<DepositCopyField label='Deposit amount' value={amount} />

				{metaMaskError ? <p className='text-center text-sm text-destructive'>{metaMaskError}</p> : null}

				<button
					type='button'
					onClick={() => {
						void payWithMetaMask()
					}}
					disabled={isMetaMaskSubmitting}
					className={getDepositActionButtonClassName(!isMetaMaskSubmitting)}
				>
					<span>Pay with MetaMask</span>
					<span className='bg-current/50 mt-0.5 h-0.5 w-8 rounded-full' aria-hidden />
				</button>
			</div>
		)
	}

	if (provider === 'phantom' && metamaskAddress) {
		return (
			<div className='flex min-h-0 flex-1 flex-col gap-5'>
				<p className='text-center text-sm leading-6 tracking-[-0.21px] text-muted-foreground'>
					Transfer {amount} {currency.currency_code} on the {currency.network} network to the admin wallet address below using Phantom.
				</p>

				<DepositCopyField label={`Admin ${currency.currency_code} deposit address`} value={metamaskAddress} />
				<DepositCopyField label='Deposit amount' value={amount} />

				{phantomError ? <p className='text-center text-sm text-destructive'>{phantomError}</p> : null}

				<button
					type='button'
					onClick={() => {
						void payWithPhantom()
					}}
					disabled={isPhantomSubmitting}
					className={getDepositActionButtonClassName(!isPhantomSubmitting)}
				>
					<span>Pay with Phantom</span>
					<span className='bg-current/50 mt-0.5 h-0.5 w-8 rounded-full' aria-hidden />
				</button>
			</div>
		)
	}

	return (
		<div className='flex min-h-0 flex-1 items-center justify-center'>
			<p className='text-sm text-muted-foreground'>Deposit details are not available for {currencyLabel}.</p>
		</div>
	)
}
